import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { pbAuthedFetch } from "../authHeaders";
import { ReportPreview } from '../components/ReportPreview';
import type { ReportDoc } from '../components/ReportPreview';
import { ReportSections } from '../components/ReportSections';
import { ReportHistory } from '../components/ReportHistory';
import { ReportSchedules } from '../components/ReportSchedules';
import '../styles/reports-bi.css';

// Types ──────────────────────────────────────────────────────────────────────

interface SectionDef {
  key: string;
  label: string;
  default: boolean;
  grant: string;
}

interface ReportType {
  key: string;
  label: string;
  scopes: string[];
  sections: SectionDef[];
}

interface Limits {
  maxScopeProjects: number;
  maxShareDays: number;
  maxNoteChars: number;
  cadences: string[];
}

interface Preset {
  id: string;
  name: string;
  config: Record<string, unknown>;
}

interface Job {
  id: string;
  status: string;
  error?: string;
}

interface NarrativeData {
  text: string;
  source: string;
  editedBy: string | null;
}

interface Project {
  id: string;
  name?: string;
}

// Constants ──────────────────────────────────────────────────────────────────

const PERIODS: [string, string][] = [
  ['this-month', 'This month'],
  ['last-30', 'Last 30 days'],
  ['qtd', 'Quarter to date'],
  ['fytd', 'FY to date'],
  ['custom', 'Custom range…'],
];

const FORMATS: [string, string][] = [
  ['pdf', 'PDF'],
  ['xlsx', 'Excel'],
  ['pptx', 'PowerPoint'],
  ['docx', 'Word'],
  ['csv', 'CSV'],
  ['json', 'JSON'],
];

const PANELS: [string, string][] = [
  ['preview', 'Preview'],
  ['history', 'History'],
  ['schedule', 'Schedule'],
];

const PREVIEW_DEBOUNCE_MS = 400;
const JOB_POLL_MS = 1200;
const MAX_JOB_POLLS = 100;

const FALLBACK_LIMITS: Limits = {
  maxScopeProjects: 25,
  maxShareDays: 30,
  maxNoteChars: 500,
  cadences: ['weekly', 'monthly', 'quarterly'],
};

// Helpers ────────────────────────────────────────────────────────────────────

function filenameFrom(disposition: string | null, fallback: string): string {
  const m = /filename="([^"]+)"/.exec(disposition || '');
  return m ? m[1] : fallback;
}

// Page ───────────────────────────────────────────────────────────────────────

export function ReportsPage() {
  const [catalog, setCatalog] = useState<ReportType[] | null>(null);
  const [limits, setLimits] = useState<Limits>(FALLBACK_LIMITS);
  const [typeKey, setTypeKey] = useState('');
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const [period, setPeriod] = useState('last-30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [order, setOrder] = useState<string[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [compare, setCompare] = useState(false);
  const [narrative, setNarrative] = useState<NarrativeData | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [presetName, setPresetName] = useState('');
  const [panel, setPanel] = useState('preview');
  const [report, setReport] = useState<ReportDoc | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busyFormat, setBusyFormat] = useState<string | null>(null);
  const [job, setJob] = useState<Job | null>(null);
  const [polls, setPolls] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const type = useMemo(
    () => (catalog || []).find((t) => t.key === typeKey) || null,
    [catalog, typeKey]
  );

  // ── catalog ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    pbAuthedFetch('/api/reports/catalog')
      .then((res) => (res && res.ok ? res.json() : null))
      .then((body) => {
        if (!live) return;
        const list: ReportType[] = (body && body.catalog) || [];
        setCatalog(list);
        if (body && body.limits) setLimits({ ...FALLBACK_LIMITS, ...body.limits });
        if (list.length) {
          setTypeKey(list[0].key);
          const defaults = list[0].sections.filter((s) => s.default).map((s) => s.key);
          setChosen(defaults);
          setOrder(defaults);
        }
      })
      .catch(() => {
        if (live) setCatalog([]);
      });
    return () => {
      live = false;
    };
  }, []);

  const loadPresets = useCallback(() => {
    pbAuthedFetch('/api/reports/presets')
      .then((res) => (res && res.ok ? res.json() : null))
      .then((body) => setPresets((body && body.presets) || []))
      .catch(() => setPresets([]));
  }, []);

  useEffect(() => {
    loadPresets();
  }, [loadPresets]);

  const pickType = useCallback(
    (key: string) => {
      setTypeKey(key);
      const t = (catalog || []).find((x) => x.key === key);
      const defaults = t ? t.sections.filter((s) => s.default).map((s) => s.key) : [];
      setChosen(defaults);
      setOrder(defaults);
      setNotes({});
      setNarrative(null);
      if (t && !t.scopes.includes('one')) setProjectIds([]);
    },
    [catalog]
  );

  const toggleSection = useCallback((key: string) => {
    setChosen((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev.concat(key)));
    setOrder((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : prev.concat(key)));
    setNotes((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const moveSection = useCallback((key: string, delta: number) => {
    setOrder((prev) => {
      const n = prev.indexOf(key);
      const to = n + delta;
      if (n === -1 || to < 0 || to >= prev.length) return prev;
      const next = prev.slice();
      next.splice(to, 0, next.splice(n, 1)[0]);
      return next;
    });
  }, []);

  const setNote = useCallback((key: string, value: string) => {
    setNotes((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleProject = useCallback(
    (id: string) => {
      setProjectIds((prev) => {
        if (prev.includes(id)) return prev.filter((x) => x !== id);
        if (prev.length >= limits.maxScopeProjects) {
          showToast(`At most ${limits.maxScopeProjects} projects in one report`);
          return prev;
        }
        return prev.concat(id);
      });
    },
    [limits.maxScopeProjects, showToast]
  );

  const requestBody = useCallback((): Record<string, unknown> | null => {
    const body: Record<string, unknown> = {
      type: typeKey,
      sections: chosen,
      projectIds,
      compare,
      order: order.filter((k) => chosen.includes(k)),
      notes: Object.fromEntries(
        Object.entries(notes).filter(([k, v]) => chosen.includes(k) && v && v.trim())
      ),
    };
    if (narrative && narrative.text) body.narrative = narrative;
    if (period === 'custom') {
      if (!customFrom || !customTo) return null;
      body.period = { from: customFrom, to: customTo };
    } else {
      body.period = period;
    }
    return body;
  }, [typeKey, chosen, projectIds, period, customFrom, customTo, compare, order, notes, narrative]);

  // ── preview ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!typeKey || !chosen.length) {
      setReport(null);
      return undefined;
    }
    const body = requestBody();
    if (!body) return undefined;

    const timer = setTimeout(() => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setPreviewing(true);
      pbAuthedFetch('/api/reports/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ctrl.signal,
      })
        .then(async (res) => {
          if (!res) throw new Error('no response');
          const payload = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(payload.error || `HTTP ${res.status}`);
          setReport(payload.report);
          setError(null);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return;
          setError(err.message || 'Could not build the preview');
        })
        .finally(() => setPreviewing(false));
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [typeKey, chosen, projectIds, period, customFrom, customTo, compare, order, notes, narrative, requestBody]);

  // ── download ────────────────────────────────────────────────────────────
  const download = useCallback(
    async (format: string) => {
      const body = requestBody();
      if (!body) {
        showToast('Enter both dates for a custom range');
        return;
      }
      setBusyFormat(format);
      try {
        const res = await pbAuthedFetch('/api/reports/generate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, format }),
        });
        if (!res || !res.ok) {
          const payload = res ? await res.json().catch(() => ({})) : {};
          throw new Error(payload.error || `HTTP ${res ? res.status : 'network'}`);
        }
        const blob = await res.blob();
        const name = filenameFrom(res.headers.get('Content-Disposition'), `report.${format}`);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        showToast(`${name} downloaded`);
      } catch (err) {
        showToast(`Could not generate the ${format.toUpperCase()} — ${(err as Error).message}`);
      } finally {
        setBusyFormat(null);
      }
    },
    [requestBody, showToast]
  );

  // ── background generation ───────────────────────────────────────────────
  const generateInBackground = useCallback(
    async (format: string) => {
      const body = requestBody();
      if (!body) {
        showToast('Enter both dates for a custom range');
        return;
      }
      try {
        const res = await pbAuthedFetch('/api/reports/jobs', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, format }),
        });
        const payload = res ? await res.json().catch(() => ({})) : {};
        if (!res || res.status !== 202) throw new Error(payload.error || 'Could not queue it');
        setPolls(0);
        setJob(payload.job);
      } catch (err) {
        showToast((err as Error).message);
      }
    },
    [requestBody, showToast]
  );

  useEffect(() => {
    if (!job || job.status === 'done' || job.status === 'error') return undefined;
    if (polls >= MAX_JOB_POLLS) {
      showToast('Still working — check History in a moment');
      setJob(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      pbAuthedFetch(`/api/reports/jobs/${job.id}`)
        .then((res) => (res && res.ok ? res.json() : null))
        .then((body) => {
          if (!body || !body.job) return;
          setPolls((n) => n + 1);
          setJob(body.job);
          if (body.job.status === 'done') {
            showToast('Report ready — see History');
            setPanel('history');
          }
          if (body.job.status === 'error') showToast(`Report failed — ${body.job.error || ''}`);
        })
        .catch(() => setPolls((n) => n + 1));
    }, JOB_POLL_MS);
    return () => clearTimeout(timer);
  }, [job, polls, showToast]);

  // ── narrative ───────────────────────────────────────────────────────────
  const draftNarrative = useCallback(async () => {
    const body = requestBody();
    if (!body) return;
    setDrafting(true);
    try {
      const res = await pbAuthedFetch('/api/reports/narrative', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const payload = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) {
        throw new Error(
          payload.error === 'ai_unconfigured'
            ? 'The assistant is not configured in this environment'
            : 'Could not draft the narrative'
        );
      }
      setNarrative({ text: payload.text, source: 'ai', editedBy: null });
    } catch (err) {
      showToast((err as Error).message);
    } finally {
      setDrafting(false);
    }
  }, [requestBody, showToast]);

  const editNarrative = useCallback((text: string) => {
    setNarrative((prev) => ({ ...(prev || { text: '', source: 'ai', editedBy: null }), text, editedBy: 'me' }));
  }, []);

  // ── presets ─────────────────────────────────────────────────────────────
  const savePreset = useCallback(async () => {
    const body = requestBody();
    if (!body || !presetName.trim()) return;
    const res = await pbAuthedFetch('/api/reports/presets', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: presetName.trim(), config: body }),
    });
    const payload = res ? await res.json().catch(() => ({})) : {};
    if (res && res.ok) {
      setPresetName('');
      loadPresets();
      showToast(`Saved "${payload.preset.name}"`);
    } else {
      showToast(payload.error || 'Could not save the preset');
    }
  }, [requestBody, presetName, loadPresets, showToast]);

  const applyPreset = useCallback((preset: Preset) => {
    const c = (preset.config || {}) as Record<string, unknown>;
    setTypeKey((c.type as string) || '');
    setChosen((c.sections as string[]) || []);
    setOrder((c.order as string[]) && (c.order as string[]).length ? (c.order as string[]) : (c.sections as string[]) || []);
    setProjectIds((c.projectIds as string[]) || []);
    setCompare(c.compare === true);
    setNotes({});
    setNarrative(null);
    if (typeof c.period === 'string') {
      setPeriod(c.period);
    } else if (c.period && (c.period as Record<string, string>).from) {
      setPeriod('custom');
      setCustomFrom((c.period as Record<string, string>).from);
      setCustomTo((c.period as Record<string, string>).to || '');
    }
  }, []);

  const onTabKey = useCallback(
    (e: React.KeyboardEvent) => {
      const keys: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1 };
      const at = PANELS.findIndex(([k]) => k === panel);
      let next: number | null = null;
      if (keys[e.key] !== undefined) next = (at + keys[e.key] + PANELS.length) % PANELS.length;
      else if (e.key === 'Home') next = 0;
      else if (e.key === 'End') next = PANELS.length - 1;
      if (next === null) return;
      e.preventDefault();
      setPanel(PANELS[next][0]);
      const buttons = tabsRef.current ? tabsRef.current.querySelectorAll('[role="tab"]') : [];
      if (buttons[next]) (buttons[next] as HTMLElement).focus();
    },
    [panel]
  );

  // Projects list - fetch from API or use empty
  const [projects] = useState<Project[]>([]);
  const canScope = !!(type && type.scopes.includes('one'));
  const ready = !!typeKey && chosen.length > 0;

  if (catalog && !catalog.length) {
    return (
      <div className="rpt-page bi-console" data-testid="reports-page">
        <div className="state">
          <p className="state__body">
            No report types are available to you. Reports are built from the pages you have been
            granted.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rpt-page bi-console" id="page-reports" data-testid="reports-page">
      {toast && (
        <div className="notice notice--info" style={{ marginBottom: 'var(--sp-4)' }}>
          {toast}
        </div>
      )}
      <div className="pagehead">
        <div className="pagehead__row">
          <div className="pagehead__text">
            <p className="pagehead__eyebrow">
              Build, schedule, and share status packs · configure a report and download or send it
            </p>
            <h1 className="pagehead__title">Reports</h1>
          </div>
        </div>
      </div>
      <div className="rpt-grid">
        {/* ── config rail ─────────────────────────────────────────────── */}
        <div className="rpt-config box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Report builder</p>
              <h2 className="box__title">Configure</h2>
            </div>
          </div>
          <div className="rpt-config-body box__body">
            {presets.length > 0 && (
              <div className="rpt-field">
                <span className="rpt-label">Saved reports</span>
                <div className="rpt-buttons">
                  {presets.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      className="btn btn--ghost btn--sm"
                      onClick={() => applyPreset(p)}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="rpt-field">
              <span className="rpt-label">Report type</span>
              <select value={typeKey} onChange={(e) => pickType(e.target.value)}>
                {(catalog || []).map((t) => (
                  <option value={t.key} key={t.key}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            <fieldset
              className="rpt-field rpt-scope"
              disabled={!canScope}
              aria-describedby={!canScope && type ? 'rpt-project-hint' : undefined}
            >
              <legend className="rpt-label">Projects</legend>
              <label className="rpt-check">
                <input
                  type="checkbox"
                  checked={projectIds.length === 0}
                  onChange={() => setProjectIds([])}
                />
                <span>All projects</span>
              </label>
              {projects.map((p) => (
                <label className="rpt-check" key={p.id}>
                  <input
                    type="checkbox"
                    checked={projectIds.includes(p.id)}
                    onChange={() => toggleProject(p.id)}
                  />
                  <span>
                    {p.id} · {p.name || p.id}
                  </span>
                </label>
              ))}
            </fieldset>
            {!canScope && type && (
              <p className="rpt-hint" id="rpt-project-hint">
                {type.label} covers the whole portfolio.
              </p>
            )}
            {canScope && projectIds.length > 1 && (
              <p className="rpt-hint">
                {projectIds.length} of at most {limits.maxScopeProjects} projects.
              </p>
            )}

            <label className="rpt-field">
              <span className="rpt-label">Period</span>
              <select value={period} onChange={(e) => setPeriod(e.target.value)}>
                {PERIODS.map(([v, l]) => (
                  <option value={v} key={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>

            {period === 'custom' && (
              <div className="rpt-range">
                <label className="rpt-field">
                  <span className="rpt-label">From</span>
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                  />
                </label>
                <label className="rpt-field">
                  <span className="rpt-label">To</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                  />
                </label>
              </div>
            )}

            <label className="rpt-check rpt-compare">
              <input
                type="checkbox"
                checked={compare}
                onChange={(e) => setCompare(e.target.checked)}
              />
              <span>Compare with the previous period</span>
            </label>
            <p className="rpt-hint">
              Adds each figure&rsquo;s movement against the equal-length window immediately before,
              and lists what moved most.
            </p>

            <ReportSections
              sections={type ? type.sections : []}
              chosen={chosen}
              order={order}
              notes={notes}
              maxNoteChars={limits.maxNoteChars}
              onToggle={toggleSection}
              onMove={moveSection}
              onNote={setNote}
            />

            <div className="rpt-field">
              <span className="rpt-label">Narrative</span>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={!ready || drafting}
                aria-busy={drafting}
                onClick={draftNarrative}
              >
                {drafting ? 'Drafting…' : 'Draft with AI'}
              </button>
              {narrative && (
                <>
                  <textarea
                    className="rpt-narrative-edit"
                    value={narrative.text}
                    rows={5}
                    onChange={(e) => editNarrative(e.target.value)}
                    aria-label="Report narrative"
                  />
                  <p className="rpt-hint">
                    Edit before it ships — the report records whether a person reviewed it.
                  </p>
                </>
              )}
            </div>

            <div className="rpt-formats">
              <span className="rpt-label">Download as</span>
              <div className="rpt-buttons">
                {FORMATS.map(([v, l]) => (
                  <button
                    type="button"
                    key={v}
                    className="btn btn--primary btn--sm"
                    disabled={!ready || busyFormat !== null}
                    aria-busy={busyFormat === v}
                    onClick={() => download(v)}
                  >
                    {busyFormat === v ? 'Generating…' : l}
                  </button>
                ))}
              </div>
              {!ready && <p className="rpt-hint">Choose at least one section.</p>}
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={!ready || (job && job.status !== 'done' && job.status !== 'error')}
                aria-busy={!!(job && job.status !== 'done' && job.status !== 'error')}
                onClick={() => generateInBackground('pdf')}
              >
                {job && job.status !== 'done' && job.status !== 'error'
                  ? 'Working in the background…'
                  : 'Generate in the background'}
              </button>
            </div>

            <div className="rpt-field">
              <span className="rpt-label">Save this configuration</span>
              <input
                type="text"
                value={presetName}
                maxLength={80}
                onChange={(e) => setPresetName(e.target.value)}
                aria-label="Preset name"
                placeholder="Monthly status pack"
              />
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                disabled={!ready || !presetName.trim()}
                onClick={savePreset}
              >
                Save
              </button>
            </div>
          </div>
        </div>

        {/* ── preview / history / schedule ────────────────────────────── */}
        <div className="rpt-preview box">
          <div className="box__head">
            <div className="box__head-text">
              <p className="box__eyebrow">Report builder</p>
              <h2 className="box__title">Output</h2>
            </div>
          </div>
          <div className="box__body">
            <div className="rpt-preview-bar">
              <div
                className="rpt-tabs"
                role="tablist"
                aria-label="Reports panel"
                ref={tabsRef}
                onKeyDown={onTabKey}
              >
                {PANELS.map(([key, label]) => (
                  <button
                    type="button"
                    key={key}
                    role="tab"
                    id={`rpt-tab-${key}`}
                    aria-selected={panel === key}
                    aria-controls={`rpt-panel-${key}`}
                    tabIndex={panel === key ? 0 : -1}
                    className={'rpt-tab' + (panel === key ? ' rpt-tab-on' : '')}
                    onClick={() => setPanel(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {previewing && panel === 'preview' && <span className="rpt-hint">Refreshing…</span>}
            </div>

            {error && panel === 'preview' && (
              <p className="rpt-error" role="status">
                {error}
              </p>
            )}

            <div
              role="tabpanel"
              id={`rpt-panel-${panel}`}
              aria-labelledby={`rpt-tab-${panel}`}
              aria-live={panel === 'preview' ? 'polite' : undefined}
              aria-busy={panel === 'preview' && previewing ? 'true' : 'false'}
            >
              {panel === 'preview' &&
                (report ? (
                  <ReportPreview report={report} />
                ) : (
                  <div className="state">
                    <p className="state__body">
                      {error
                        ? error
                        : ready
                          ? 'Building the preview…'
                          : 'Choose a report type and at least one section.'}
                    </p>
                  </div>
                ))}
              {panel === 'history' && (
                <ReportHistory maxShareDays={limits.maxShareDays} showToast={showToast} />
              )}
              {panel === 'schedule' && (
                <ReportSchedules
                  cadences={limits.cadences}
                  currentConfig={requestBody}
                  format="pdf"
                  showToast={showToast}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
